import { jwtDecode } from 'jwt-decode';
import GrabStorage from 'grab-storage';
import { camelCase, isDevEnv } from 'src/utils/index';

/** @deprecated - Not used in codebase */
export const TOKEN_HEADER_KEY = 'X-XSRF-TOKEN';
/** @deprecated - Not used in codebase */
export const KC_TOKEN_KEY = '__KC_TOKEN__';
/** @deprecated - Not used in codebase */
export const TOKEN_EMPTY_ERROR = 'TOKEN_EMPTY_ERROR';
export const TOKEN_NAME = 'JWT';
/** @deprecated */
export const USER_PROFILE_INFO = 'userprofileInfo';
/** @deprecated - Not used in codebase */
export const REFRESH_TOKEN = '__REFRESH_TOKEN__';
/** @deprecated - Not used in codebase */
export const CLIENT_TOKEN = '__CLIENT_TOKEN__';
/** @deprecated - Not used in codebase */
export const CONCEDO_STATE = '__CONCEDO_STATE__';
/** @deprecated - Not used in codebase */
export const CONCEDO_ENABLE = '__CONCEDO_ENABLE__';
/** @deprecated - Not used in codebase */
export const CONCEDO_REDIRECT_URL = '__CONCEDO_REDIRECT_URL__'; // This URL will be redirected to after login successfully
/** @deprecated - Not used in codebase */
export const CONCEDO_REFRESH_TIME_ID = '__CONCEDO_REFRESH_TIME_ID__'; // This URL will be redirected to after login successfully
/** @deprecated - Not used in codebase */
export const UNAUTH_ERROR_CODE = 'UNAUTHORIZED';
/** @deprecated - Not used in codebase */
export const SWITCH_CONCEDO_ALERT_CODE = 'SWITCH_CONCEDO_ALERT_CODE';
/** @deprecated - Not used in codebase */
export const SWITCH_GOOGLE_ALERT_CODE = 'SWITCH_GOOGLE_ALERT_CODE';
/** @deprecated - Not used in codebase */
export const SWITCH_AUTH_ERROR = 'Authentication method is changed';

/** @deprecated - Not used in codebase */
export const enableMexPortalWebloginExperience = 'enableMexPortalWebloginExperience';

let mobileCookieTokenAuthEnabled = false;

export type DecodedTokenType = Partial<{
  esi: string;
  exp: number;
  iat: number;
  jti: string;
  lme: string;
  name: string;
  sub: string;
}>;

export type UserProfileData = {
  userId?: string;
  userName?: string;
  role?: string;
};

export const setMobileCookieTokenAuthEnabled = (enabled: boolean): void => {
  mobileCookieTokenAuthEnabled = enabled;
};

export const isMobileCookieTokenAuthEnabled = (): boolean => mobileCookieTokenAuthEnabled;

export const isCookieAuthEnabled = (): boolean => {
  try {
    const jwtToken = getToken();
    const webloginFlagInStorage = GrabStorage.getItem(enableMexPortalWebloginExperience);
    const userProfileInfo = GrabStorage.getItem(USER_PROFILE_INFO);

    // Cookie auth is enabled if:
    // 1. JWT token doesn't exist (not using JWT-based auth)
    // 2. AND (weblogin flag is set OR userprofileInfo exists, indicating a cookie session)
    // Under normal circumstances, if we're using cookie authn, the JWT itself would be empty. But
    // if JWT is present, it would mean that we're trying to fallback to the legacy flow, this is
    // because for WebView use cases the session is injected from MEX app.
    const result = !jwtToken && (!!webloginFlagInStorage || !!userProfileInfo);
    return result;
  } catch (error) {
    console.error('Error in isCookieAuthEnabled:', error);
    return false;
  }
};

export function isTokenExpired(token?: string): boolean {
  // If cookie auth is enabled, skip token expiration check.
  if (isCookieAuthEnabled() || isMobileCookieTokenAuthEnabled()) {
    return false;
  }
  const isDev = isDevEnv();
  if (isDev) {
    return false;
  }
  const decoded = getDecodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }
  const now = Math.floor(Date.now() / 1000);
  const expired = decoded.exp < now;
  return expired;
}

/** @deprecated - Not used in codebase */
export function setConcedoState(state: string | number): void {
  GrabStorage.setItem(CONCEDO_STATE, state);
}

/** @deprecated - Not used in codebase */
export function getConcedoState(): string | number | undefined {
  return GrabStorage.getItem(CONCEDO_STATE);
}

/** @deprecated - Not used in codebase */
export function removeConcedoState(): void {
  GrabStorage.removeItem(CONCEDO_STATE);
}

export function getToken(): string | undefined {
  return GrabStorage.getItem(TOKEN_NAME);
}

/** @deprecated - Not used in codebase */
export function getRefreshToken(): string | undefined {
  return GrabStorage.getItem(REFRESH_TOKEN);
}

/** @deprecated - Not used in codebase */
export function setToken(token: string, refreshToken = ''): void {
  GrabStorage.setItem(TOKEN_NAME, token);
  if (refreshToken) GrabStorage.setItem(REFRESH_TOKEN, refreshToken);
}

export function removeToken(): void {
  return GrabStorage.removeItem(TOKEN_NAME);
}

/** @deprecated - Use useUserProfile hook instead */
export function getUserProfileInfo(): UserProfileData {
  const userProfileInLSStr = GrabStorage.getItem(USER_PROFILE_INFO);
  if (userProfileInLSStr) {
    try {
      const userProfileInLS = JSON.parse(userProfileInLSStr);
      return {
        userId: userProfileInLS?.user_profile?.grab_id,
        userName: userProfileInLS?.user_profile?.username,
        role: userProfileInLS?.user_profile?.role,
      };
    } catch (error) {
      console.error('Failed to parse user profile info from local storage:', error);
      return {};
    }
  } else {
    return {};
  }
}

/** @deprecated - Not used in codebase */
export function removeUserProfileInfo(): void {
  return GrabStorage.removeItem(USER_PROFILE_INFO);
}

/** @deprecated - Use useUserProfile hook instead of localStorage */
export function clearUserData(): void {
  removeToken();
  removeUserProfileInfo();
}

export const camelizeToken = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map((v) => camelizeToken(v));
  }
  if (obj != null && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [camelCase(key)]: camelizeToken(obj[key]),
      }),
      {},
    );
  }
  return obj;
};

export function getDecodeToken(token?: string): DecodedTokenType {
  const existToken = token || getToken();
  if (!existToken) return {};

  try {
    const decodedToken = jwtDecode<DecodedTokenType>(existToken);
    return decodedToken;
  } catch {
    return {};
  }
}

/** @deprecated - Not used in codebase */
export const getUserName = () => {
  const decodedToken = getDecodeToken();

  if (Object.keys(decodedToken).length) {
    if (decodedToken.name) {
      return decodedToken.name.split('@')[0];
    }
    return 'anonymous';
  }

  return 'anonymous';
};

/** @deprecated - Not used in codebase */
export const getUserEmail = () => {
  const decodedToken = getDecodeToken();

  if (Object.keys(decodedToken).length) {
    if (decodedToken.name) {
      return decodedToken.name;
    }
    return 'anonymous';
  }

  return 'anonymous';
};

/** @deprecated - Not used in codebase */
export const encodeState = (key: string, redirectPath?: string): string => {
  const state = JSON.stringify({ key, redirectPath });
  return Buffer.from(state).toString('base64');
};

/** @deprecated - Not used in codebase */
export const decodeState = (encodedStr: string): { key?: string; redirectPath?: string } => {
  try {
    const decodedStr = Buffer.from(encodedStr, 'base64').toString('ascii');
    const state = JSON.parse(decodedStr);
    return state;
  } catch {
    /* Failed to decode state, return empty object */
  }

  return {};
};
